<template>
  <b-container>
    <b-alert
      :show="status.isResponseError"
      variant="danger"
      fade
      :dismissible="true"
    >
      <a href="#" class="alert-link">請求錯誤，請重新送出資料。</a>
    </b-alert>
    <b-alert show>
      您好，歡迎使用 🌸
      蝴蝶蘭種苗分級系統，該系統可以幫助您辨識植株是否符合標準。請上傳要辨識植株的側視、俯視圖片，接著點擊「開始辨識」就能獲得辨識結果，詳細分級規則可查看導覽列中的分級標準表。
    </b-alert>
    <b-row>
      <b-col cols="12" sm="6" lg="3">
        <BaseImageInput
          header="側視圖"
          class="mb-3"
          @loaded="setFormSideImage"
        />
      </b-col>
      <b-col cols="12" sm="6" lg="3">
        <BaseImageInput header="俯視圖" @loaded="setFormTopImage" />
      </b-col>
      <b-col cols="12" sm="6" lg="2">
        <b-form @submit.prevent="calculate">
          <b-form-group class="d-block m-0">
            <b-btn
              type="submit"
              variant="primary"
              :disabled="status.calculating"
            >
              開始辨識
            </b-btn>
          </b-form-group>
        </b-form>
      </b-col>

      <b-col cols="12" sm="6" lg="3">
        <b-card class="mb-3" header="辨識結果">
          <b-card-group>
            <b-card-title>
              綜合結果:
              <span v-show="!status.isDetectorResultLoad">
                <span v-if="report.qualified === null">--</span>
                <b-badge v-else-if="report.qualified" variant="success"
                  >合格</b-badge
                >
                <b-badge v-else variant="danger">不合格</b-badge>
              </span>
            </b-card-title>
            <b-spinner
              v-show="status.isDetectorResultLoad"
              variant="success"
              class="ml-2"
              label="Spinning"
            />
          </b-card-group>
          <b-card-group class="d-block">
            <b-list-group>
              <b-list-group-item>
                <BaseListItem
                  label="葉片數："
                  :loading="status.isDetectorResultLoad"
                  :format-result="formatResult"
                  :original-value="report.all_leaf.count"
                  :imageUrls="report.urls.leafCount"
                  :valid="report.all_leaf.qualified"
                />
              </b-list-group-item>
              <b-list-group-item>
                <BaseListItem
                  label="植株大小 (cm)："
                  :loading="status.isDetectorResultLoad"
                  :format-result="formatResult"
                  :original-value="report.plant.shape.long_side"
                  :imageUrls="report.urls.plantShape"
                  :valid="report.plant.shape.qualified"
                />
              </b-list-group-item>
              <b-list-group-item>
                <BaseListItem
                  label="葉序角度："
                  :loading="status.isDetectorResultLoad"
                  :format-result="formatResult"
                  :original-value="report.plant.aspect_ratio.value"
                  :imageUrls="report.urls.plantAspectRatio"
                  :valid="report.plant.aspect_ratio.qualified"
                />
              </b-list-group-item>
              <b-list-group-item>
                <BaseListItem
                  label="最大葉片長寬比："
                  :loading="status.isDetectorResultLoad"
                  :format-result="formatResult"
                  :original-value="report.large_leaf.aspect_ratio"
                  :imageUrls="report.urls.largeLeaf"
                  :valid="report.large_leaf.qualified"
                />
              </b-list-group-item>
            </b-list-group>
          </b-card-group>
        </b-card>
      </b-col>
    </b-row>
    <b-overlay no-wrap variant="light" :show="status.initLoading" rounded="sm">
      <template #overlay>
        <div class="text-center">
          <p id="cancel-label">
            模型載入中...
          </p>
          <b-spinner v-show="true" variant="success" label="Spinning" />
        </div>
      </template>
    </b-overlay>
  </b-container>
</template>

<script>
import Multiselect from "vue-multiselect"; // 多選 Multiselect 元件導入
import axios from "axios";
import BaseImageInput from "../components/BaseImageInput";
import BaseListItem from "../components/BaseListItem";
import "vue-multiselect/dist/vue-multiselect.min.css";
import report from "../mock/report.json";
import mixin from "../lib/mixin.js";
import util from "../lib/util.js";

export default {
  name: "Home",
  components: {
    BaseImageInput,
    BaseListItem,
    Multiselect
  },
  mixins: [mixin],
  data() {
    return {
      access_token: null, // 呼叫桃改專家系統
      form: {
        model: {}, // 選擇的物件辨識模型
        topImage: null, // 俯視圖
        sideImage: null // 側視圖
      },
      report: {
        plant: {
          shape: {
            long_side: null
          },
          aspect_ratio: {
            value: null
          }
        },
        large_leaf: {},
        all_leaf: {},
        images: [],
        urls: {
          leafCount: [],
          plantShape: [],
          plantAspectRatio: [],
          largeLeaf: []
        },
        qualified: null
      },

      // 狀態的控制
      status: {
        initLoading: false, // 畫面初始化中
        isDetectorResultLoad: false, // 影像辨識處理中
        calculating: false, // 計算結果中
        isResponseError: false // 回應發生錯誤
      }
    };
  },
  async created() {
    console.log("ML_SERVICE:", process.env.ML_SERVICE);
    this.access_token = this.getAccessToken();
    this.status.initLoading = false;
  },
  methods: {
    formatResult(value) {
      return util.isNumber(value) ? value : "--";
    },
    setFormTopImage(file) {
      this.form.topImage = file;
    },
    setFormSideImage(file) {
      this.form.sideImage = file;
    },
    formValidation() {
      if (!this.form.topImage || !this.form.sideImage)
        throw Error("請上傳照片資料");
    },
    async calculate() {
      try {
        this.formValidation();
        this.status.calculating = true;
        this.status.isResponseError = false;

        await this.getRatingReport();
      } catch (error) {
        console.error(error);
        this.status.isResponseError = true;
      } finally {
        this.status.calculating = false;
      }
    },
    async getRatingReport() {
      try {
        this.status.isDetectorResultLoad = true;
        console.time("rating");

        const form = new FormData();
        form.append("top_image", this.form.topImage);
        form.append("side_image", this.form.sideImage);
        form.append("resize", "600");

        const { data } = await axios.request({
          method: "post",
          url: `${process.env.ML_SERVICE}/flower/phalaenopsis-seedling/report`,
          headers: { "content-type": "multipart/form-data" },
          data: form,
          params: {
            hashkey: this.getHash()
          }
        });
        console.timeEnd("rating");

        this.report = data;
        this.report.urls = {
          leafCount: [data.images[1]],
          plantShape: [data.images[4]],
          plantAspectRatio: [data.images[2]],
          largeLeaf: [data.images[3], data.images[0]]
        };
      } catch (err) {
        console.error(err);
      } finally {
        this.status.isDetectorResultLoad = false;
      }
    }
  }
};
</script>
